import { computed } from 'vue';
import { useStore } from 'vuex';
import { apiRequest } from '@/api/http';
import { titleCase } from '@/utils/format';
import { useAsyncAction } from './useAsyncAction';
import { useConfirm } from './useConfirm';

// Announcement API rows → <UiPostFeed> posts.
export function toFeedPosts(rows) {
  return (rows || []).map((p) => ({
    id: p.postID,
    author: titleCase(p.teacherName),
    date: p.date,
    text: p.text,
    authorId: p.teacherID,
    comments: (p.comments || []).map((c) => ({ id: c.id, userId: c.userID, name: c.name, text: c.title })),
  }));
}

// Comment on an announcement, or delete a comment; `onDone` reloads the
// feed. The server takes the commenter from the sign-in and checks that
// they belong to the class.
export function useAnnouncementComments(onDone) {
  const store = useStore();
  const myId = computed(() => Number(store.state.user?.id));
  const { busy, run } = useAsyncAction();
  const { confirm } = useConfirm();

  async function comment(post, text) {
    const payload = { title: text, postID: post.id };
    const ok = await run(() => apiRequest('post', '/announcement/addComment', { data: JSON.stringify(payload) }), {
      error: 'Could not add the comment.',
    });
    if (ok) onDone?.();
  }

  // The post's author (the adviser) can remove any comment on it; anyone
  // can remove their own.
  const canDelete = (post, c) => Number(c.userId) === myId.value || Number(post.authorId) === myId.value;

  async function removeComment(post, c) {
    const ok = await confirm({
      title: 'Delete comment?',
      description: `"${c.text.length > 80 ? `${c.text.slice(0, 77)}…` : c.text}" will be removed for everyone.`,
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    if (await run(() => apiRequest('delete', `/announcement/comment/${c.id}`), { success: 'Comment deleted.', error: 'Could not delete the comment.' })) {
      onDone?.();
    }
  }

  return { sending: busy, comment, canDelete, removeComment };
}
